/**
 * RoleBadge component for displaying user's active team role.
 *
 * Contract: UI_AUTHORIZATION_FEEDBACK_CONTRACT_V1.md v1.2
 * Requirement: INV-UI-12 - Global role indicator
 *
 * Shows the user's current role in the active team.
 * Must be visible at all times in the TopBar.
 */

import React from 'react';
import { Shield } from 'lucide-react';
import { useTeam } from '@/contexts/TeamContext';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';

/**
 * Get role badge color classes
 */
function getRoleBadgeClasses(role: string | null): string {
  if (!role) {
    return 'bg-muted text-muted-foreground';
  }

  // Owner/Admin get primary color
  if (role === 'owner' || role === 'admin') {
    return 'bg-primary/10 text-primary border-primary/20';
  }

  // Manager gets secondary color
  if (role === 'manager') {
    return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900';
  }

  // Viewer gets warning color (restricted access)
  if (role === 'viewer') {
    return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900';
  }

  // Default for dealer/mechanic/member
  return 'bg-muted text-muted-foreground border-border';
}

/**
 * RoleBadge component
 *
 * Displays the user's active team role in the TopBar.
 * Visible on both mobile and desktop (INV-UI-12).
 * Mobile: compact pill without icon
 * Desktop: full badge with icon
 */
export function RoleBadge() {
  const { role, loading } = useTeam();
  const { t } = useTranslation('settings');

  // Loading state
  if (loading) {
    return <Skeleton className="h-7 w-16 sm:w-20" />;
  }

  // Get translated role label with safe fallback
  const roleLabel = role
    ? t(`teamMembers.roles.${role}`, { defaultValue: role })
    : t('teamMembers.roles.viewer', { defaultValue: 'No Role' });
  const badgeClasses = getRoleBadgeClasses(role);

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2 sm:px-2.5 py-1 text-xs font-medium ${badgeClasses}`}
      role="status"
      aria-label={`Your role: ${roleLabel}`}
    >
      <Shield className="hidden sm:inline h-3.5 w-3.5" aria-hidden="true" />
      <span className="text-[11px] sm:text-xs">{roleLabel}</span>
    </div>
  );
}
